import {Injectable} from '@angular/core';
import SewingPatternProject from '../../models/sewing-pattern-project.model';
import FabricInterface from '../../models/fabric.interface';

import {FilesystemDirectory, Plugins} from '@capacitor/core';
import PatternAreaInterface from '../../models/pattern-area.interface';
import {Platform, ToastController} from '@ionic/angular';
import rgb2hex from 'rgb2hex';
import SewingPatternInterface from '../../models/sewing-pattern.interface';
import {PatternRepository} from '../../repositories/pattern.repository';

@Injectable({
    providedIn: 'root'
})
export class PatternTransformService {

    public temporaryPatternProject: SewingPatternProject;
    public currentPatternProject: SewingPatternProject;
    public currentPatternIsTemporary: boolean;

    constructor(private sewingPatternRepository: PatternRepository) {
    }

    getRequiredFabricsOfSewingPattern(sewingPattern: SewingPatternInterface) {
        return [...new Set(sewingPattern.areas.map(area => area.fabric).filter(fabric => !!fabric))];
    }

    async getRequiredFabricDefinitionsOfSewingPattern(sewingPattern: SewingPatternInterface, includeInSVG = false, prefix = '') {
        const html = [];
        for (const fabric of this.getRequiredFabricsOfSewingPattern(sewingPattern)) {

            let image = fabric.path;

            if (includeInSVG) {
                image = await this.downloadImageAndTransformToBase64(image);
            }

            html.push(`
                <pattern id="${prefix}${fabric.id}" viewBox="0,0,${fabric.width},${fabric.height}" width="${sewingPattern.width}" height="${sewingPattern.height}" preserveAspectRatio="none" patternUnits="userSpaceOnUse">
                    <image x="0" y="0" width="${fabric.width}" height="${fabric.height}" xlink:href="${image}" />
                </pattern>
            `);
        }

        return html.join('');
    }

    async generateFinalSewingPatternSVG(sewingPattern: SewingPatternInterface, includeShadow = true, prefix = '') {
        const svgContainer = document.createElement('div');
        svgContainer.innerHTML = await this.sewingPatternRepository.loadSVGFromPattern(sewingPattern);

        svgContainer.style.setProperty('display', 'none');

        document.body.append(svgContainer);

        const svg = svgContainer.querySelector('svg') as SVGElement;
        svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        svg.querySelector('defs').innerHTML += await this.getRequiredFabricDefinitionsOfSewingPattern(sewingPattern, true, prefix);

        this.prepareSvgGroups(svg, sewingPattern.areas, (group: SVGElement, area: PatternAreaInterface) => {
            if (area.fabric) {
                group.style.setProperty('fill', `url(#${prefix}${area.fabric.id})`);
            }
        });

        if (includeShadow) {
            const shadowSVG = await this.sewingPatternRepository.loadShadowSVGFromPattern(sewingPattern);

            if (shadowSVG) {
                const shadow = document.createElement('div');
                shadow.innerHTML = shadowSVG;

                // @ts-ignore
                const innerShadow = [...shadow.children].map(child => child.outerHTML).join('');

                svg.innerHTML += innerShadow;
            }
        }

        svgContainer.remove();

        return svg.outerHTML;
    }

    transformPolygon(group: SVGElement) {
        if (group.tagName === 'polygon') {
            const svgNamespace = group.ownerSVGElement.namespaceURI;
            const path = document.createElementNS(svgNamespace, 'path');
            const pathData = `M ${group.getAttribute('points')}z`;

            path.setAttribute('d', pathData);
            path.setAttribute('fill', group.getAttribute('fill'));
            path.setAttribute('class', group.getAttribute('class'));

            group.parentNode.replaceChild(path, group);

            return path as SVGElement;
        }

        return group;
    }

    prepareSvgGroups(svgElement: SVGElement | HTMLElement, areas: PatternAreaInterface[], callback: (group: SVGElement, area: PatternAreaInterface) => void) {
        const groups = svgElement.querySelectorAll('[data-area]');

        groups.forEach((group: SVGElement) => {
            areas.forEach(area => {
                if (group.dataset.area && group.dataset.area === area.id) {
                    callback(group, area);
                    return;
                }
            });
        });
    }

    async downloadImageAndTransformToBase64(path: string): Promise<string> {
        const response = await fetch(path);
        const blob = await response.blob();

        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = function() {
                resolve(this.result as string);
            };
            reader.readAsDataURL(blob);
        });
    }
}
